import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import MilesChatCta from './MilesChatCta';
import MilesChatWindow from './MilesChatWindow';
import { useMilesChat } from '../../hooks/useMilesChat';

interface MilesChatProps {
  customerId: string | null;
  enabled?: boolean;
}

/**
 * Main MILES Chat component that manages the floating CTA and chat window.
 * Renders a collapsed button in the bottom-right corner that opens a chat window.
 */
function MilesChat({ customerId, enabled = true }: MilesChatProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const milesChat = useMilesChat({ customerId, enabled: enabled && isOpen });

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsExpanded(false);
  }, []);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Don't render if disabled or customerId is not available
  if (!enabled || !customerId) return null;

  return (
    <>
      {/* Collapsed CTA Button - only show when chat is not open */}
      {!isOpen && <MilesChatCta onClick={handleOpen} />}

      {/* Chat Window - rendered via portal for proper z-index stacking */}
      {isOpen &&
        createPortal(
          <MilesChatWindow
            isExpanded={isExpanded}
            onClose={handleClose}
            onToggleExpand={handleToggleExpand}
            {...milesChat}
          />,
          document.body
        )}
    </>
  );
}

export default MilesChat;
